/**
 * Format a monetary amount as USD (e.g. $4.85).
 * @param {number|string|null|undefined} amount
 * @returns {string}
 */
export function formatPriceAmount(amount) {
  if (amount === null || amount === undefined || amount === "") return "—";

  const value = parseFloat(amount);
  if (Number.isNaN(value)) return "—";

  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format listing/product price; returns "Free" for zero/free items.
 * @param {number|string|null|undefined} price
 * @returns {string}
 */
export function formatPrice(price) {
  if (
    price === null ||
    price === undefined ||
    price === "" ||
    price === "Free" ||
    price === "free"
  ) {
    return "Free";
  }

  const value = parseFloat(price);
  if (Number.isNaN(value) || value === 0) return "Free";

  return formatPriceAmount(value);
}

/**
 * Check if a sale is currently active based on object with sale_start_date, sale_end_date, sale_percentage (e.g. mockup/listing).
 * @param {Object} obj - Object with sale_start_date, sale_end_date, sale_percentage (typically the mockup/listing)
 * @returns {{ active: boolean, percentage: number|null }} - Whether sale is active and the percentage
 */
export function getActiveSale(obj) {
  if (!obj || obj.sale_percentage == null || obj.sale_percentage === "") return { active: false, percentage: null };
  const start = obj.sale_start_date ? new Date(obj.sale_start_date) : null;
  const end = obj.sale_end_date ? new Date(obj.sale_end_date) : null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  if (start) start.setHours(0, 0, 0, 0);
  if (end) end.setHours(23, 59, 59, 999);
  const inRange = (!start || now >= start) && (!end || now <= end);
  const percentage = parseInt(obj.sale_percentage, 10);
  return { active: inRange && !isNaN(percentage) && percentage > 0, percentage: inRange ? percentage : null };
}

/**
 * Get display values for price when sale may apply.
 * @param {number|string|null} price - Raw price (number or string)
 * @param {{ active: boolean, percentage: number|null }} saleInfo - Result of getActiveSale()
 * @returns {{ displayOriginal: number|null, displayDiscounted: number|null, hasDiscount: boolean }} - Original price, discounted price (or same as original), and whether to show strikethrough
 */
export function getSalePriceDisplay(price, saleInfo) {
  const p =
    price === null || price === undefined || price === "" || price === "Free"
      ? null
      : parseFloat(price);
  if (p === null || isNaN(p) || p === 0)
    return { displayOriginal: null, displayDiscounted: null, hasDiscount: false };
  if (saleInfo?.active && saleInfo?.percentage != null) {
    const discounted = Math.round(p * (1 - saleInfo.percentage / 100) * 100) / 100;
    return { displayOriginal: p, displayDiscounted: discounted, hasDiscount: true };
  }
  return { displayOriginal: null, displayDiscounted: p, hasDiscount: false };
}

const parseSaleDate = (createdAt) => {
  if (!createdAt) return null;

  if (/^\d{1,2}:\d{2} [AP]M$/i.test(createdAt)) {
    const today = new Date().toISOString().split("T")[0];
    const date = new Date(`${today} ${createdAt}`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Format sale/transaction date for display.
 * @param {string|null|undefined} createdAt
 * @returns {string}
 */
export function formatSaleDateTime(createdAt) {
  const date = parseSaleDate(createdAt);
  if (!date) return createdAt || "—";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function getSaleThumbnailSrc(productDetails) {
  if (!productDetails) return null;

  if (productDetails.use_main_file_as_thumbnail && productDetails.compressed_file) {
    return productDetails.compressed_file;
  }

  const thumbnail =
    productDetails.thumbnails?.[0]?.image ||
    productDetails.bundle_thumbnails?.[0]?.image;

  return thumbnail || productDetails.compressed_file || null;
}
